import { LuArrowUpDown, LuChevronDown, LuChevronUp } from "react-icons/lu";

const COLUMNS = [
  { key: "id", name: "ID", sort_value: "id", className: "w-[7%]" },
  { key: "receipt", name: "Kuponi", sort_value: "receipt_id", className: "w-[9%]" },
  { key: "user", name: "Përdoruesi", sort_value: "first_name", className: "w-[25%]" },
  { key: "game", name: "Loja", sort_value: "title", className: "w-[25%]" },
  { key: "receipts", name: "Kuponat", sort_value: "receipt_count", className: "w-[12%]", align: "right" },
  { key: "created", name: "Krijuar", sort_value: "created_at", className: "w-[18%]" },
];

const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Tirane",
    });
  } catch {
    return "—";
  }
};

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-6 py-4"><div className="h-3 w-8 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-10 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4">
      <div className="space-y-2">
        <div className="h-3 w-32 bg-gray-200 rounded" />
        <div className="h-2.5 w-48 bg-gray-100 rounded" />
      </div>
    </td>
    <td className="px-6 py-4"><div className="h-3 w-40 bg-gray-200 rounded" /></td>
    <td className="px-6 py-4"><div className="h-3 w-12 bg-gray-200 rounded ml-auto" /></td>
    <td className="px-6 py-4"><div className="h-3 w-24 bg-gray-200 rounded" /></td>
  </tr>
);

const SortIcon = ({ active, order }) => {
  if (!active)
    return (
      <LuArrowUpDown
        size={12}
        className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    );
  return order === "desc" ? (
    <LuChevronDown size={14} className="text-[var(--secondary-color)]" />
  ) : (
    <LuChevronUp size={14} className="text-[var(--secondary-color)]" />
  );
};

export default function PrizesTable({
  prizes,
  isLoading,
  params = {},
  onSort,
  onRowClick,
  onGameClick,
  onReceiptClick,
}) {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
      {/* Desktop */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((col) => {
                const sortable = !!col.sort_value;
                const active = sortable && params.sort_by === col.sort_value;
                return (
                  <th
                    key={col.key}
                    onClick={sortable ? () => onSort?.(col.sort_value) : undefined}
                    className={`group ${col.className} ${
                      col.align === "right" ? "text-right" : "text-left"
                    } px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider ${
                      active ? "text-[var(--secondary-color)]" : "text-gray-500"
                    } ${sortable ? "cursor-pointer select-none hover:text-gray-900" : ""} transition-colors`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.name}
                      {sortable && <SortIcon active={active} order={params.sort_order} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (!prizes || prizes.length === 0)
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              : prizes?.map((p, idx) => (
                  <tr
                    key={p.id ?? `${p.user_id}-${p.game_id}-${idx}`}
                    onClick={() => onRowClick?.(p)}
                    className="group cursor-pointer transition-colors hover:bg-gray-50/70"
                  >
                    <td className="px-6 py-3.5 text-gray-500 tabular-nums text-xs">
                      #{p.user_id}
                    </td>
                    <td className="px-6 py-3.5 tabular-nums text-xs">
                      {p.receipt_id ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onReceiptClick?.(p);
                          }}
                          className="text-gray-700 hover:text-[var(--secondary-color)] hover:underline"
                        >
                          #{p.receipt_id}
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="font-medium text-gray-900 truncate">
                        {(p.first_name || p.last_name)
                          ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
                          : "—"}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{p.email || "—"}</div>
                    </td>
                    <td className="px-6 py-3.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGameClick?.(p);
                        }}
                        className="text-left text-gray-700 hover:text-[var(--secondary-color)] hover:underline truncate max-w-[280px]"
                      >
                        {p.game_title || `Loja #${p.game_id}`}
                      </button>
                    </td>
                    <td className="px-6 py-3.5 text-right text-gray-900 font-semibold tabular-nums">
                      {p.receipt_count ?? "—"}
                    </td>
                    <td className="px-6 py-3.5 text-gray-600 whitespace-nowrap tabular-nums text-xs">
                      {formatDateTime(p.created_at)}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden divide-y divide-gray-100">
        {isLoading && (!prizes || prizes.length === 0) ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse p-3 rounded-xl bg-gray-50 space-y-2">
                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                <div className="h-2.5 w-3/4 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          prizes?.map((p, idx) => (
            <button
              key={p.id ?? `${p.user_id}-${p.game_id}-${idx}`}
              type="button"
              onClick={() => onRowClick?.(p)}
              className="w-full text-left p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {(p.first_name || p.last_name)
                      ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
                      : "—"}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{p.email || "—"}</div>
                </div>
                <span className="text-gray-900 font-semibold tabular-nums whitespace-nowrap">
                  {p.receipt_count ?? "—"} kupona
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onGameClick?.(p);
                  }}
                  className="text-xs text-gray-700 hover:text-[var(--secondary-color)] hover:underline truncate"
                >
                  {p.game_title || `Loja #${p.game_id}`}
                </span>
                <span className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                  {p.receipt_id && (
                    <>
                      <span
                        role="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onReceiptClick?.(p);
                        }}
                        className="hover:text-[var(--secondary-color)] hover:underline"
                      >
                        Kuponi #{p.receipt_id}
                      </span>
                      {" · "}
                    </>
                  )}
                  {formatDateTime(p.created_at)}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
